import {
	type ChangeEvent,
	type ComponentProps,
	type FocusEvent,
	type MouseEvent as ReactMouseEvent,
	type ReactNode,
	memo,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button-v25-3/new';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import CrossIcon from '@atlaskit/icon/core/cross';
import { RovoIcon } from '@atlaskit/logo';
import Lozenge from '@atlaskit/lozenge-next/new';
import { Text } from '@atlaskit/primitives-next';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import {
	itSupportHelpCards,
	portalArticles,
	teamNavigationConfig,
	type ArticleAccentColor,
	type HelpArticleId,
	type HelpCard,
} from '../../jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as messages } from '../../jsm-portal-playground-messages';
import { isUntrustedClick, useJsmPlaygroundAnalytics } from '../../use-jsm-playground-analytics';
import { JsmPlaygroundDemoCursor } from '../jsm-playground-demo-cursor';
import { centerOf, type DemoPoint, rectWithin } from '../jsm-playground-demo-geometry';
import { useJsmPlaygroundDemoLayer } from '../jsm-playground-demo-layer';
import {
	useDemoCursor,
	useDemoTimeline,
	useNotifyDemoComplete,
} from '../jsm-playground-demo-timeline';
import { JsmPlaygroundHeading } from '../jsm-playground-heading';
import { usePrefersReducedMotion } from '../jsm-playground-motion';
import { JsmPlaygroundNavigation } from '../jsm-playground-navigation';
import { JsmPlaygroundSkipAnimationButton } from '../jsm-playground-skip-animation-button';

import { JsmPortalPlaygroundArticleView } from './jsm-portal-playground-article-view';
import { DEMO_CURSOR_LABEL, HUB_DEMO } from './jsm-portal-playground-it-support-demo';
import {
	BEACON_GRADIENT,
	BRAND_FONT,
	formatCardDate,
	PortalArticleIconTile,
} from './jsm-portal-playground-it-support-shared';
import { JsmPortalPlaygroundRovoLauncherMark } from './jsm-portal-playground-rovo-chat';

const portalHeroCardHand =
	'https://dam-cdn.atl.orangelogic.com/AssetLink/0bid83rvsfdq11202jt5hcyaph73i23q.png';
const customizeSidebarLogo =
	'https://dam-cdn.atl.orangelogic.com/AssetLink/ky3tntm8xu0531s421nd2v10s4k82o4h.png';
const customizeSidebarBanner =
	'https://dam-cdn.atl.orangelogic.com/AssetLink/gfv1s3xq155yt8u84652d53wj0os8166.png';

type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const SendButtonIcon: IconButtonIcon = () => <ArrowUpIcon label="" size="medium" />;
const CloseButtonIcon: IconButtonIcon = () => <CrossIcon label="" size="medium" />;

export const ARTICLE_SWITCH_OUT_MS = 200;
export const ARTICLE_SWITCH_IN_MS = 280;
const ARTICLE_SWITCH_EASE = 'cubic-bezier(0.33, 0, 0.2, 1)';

const usePortalArticleTransition = () => {
	const prefersReducedMotion = usePrefersReducedMotion();
	const [renderedArticleId, setRenderedArticleId] = useState<HelpArticleId | null>(null);
	const [phase, setPhase] = useState<'idle' | 'out' | 'in'>('idle');
	const timersRef = useRef<number[]>([]);

	useEffect(
		() => () => {
			timersRef.current.forEach(window.clearTimeout);
			timersRef.current = [];
		},
		[],
	);

	const transitionTo = useCallback(
		(nextArticleId: HelpArticleId | null) => {
			timersRef.current.forEach(window.clearTimeout);
			timersRef.current = [];

			if (prefersReducedMotion) {
				setRenderedArticleId(nextArticleId);
				setPhase('idle');
				return;
			}

			setPhase('out');
			timersRef.current.push(
				window.setTimeout(() => {
					setRenderedArticleId(nextArticleId);
					setPhase('in');
					timersRef.current.push(window.setTimeout(() => setPhase('idle'), ARTICLE_SWITCH_IN_MS));
				}, ARTICLE_SWITCH_OUT_MS),
			);
		},
		[prefersReducedMotion],
	);

	const isOut = phase === 'out';
	const durationMs = isOut ? ARTICLE_SWITCH_OUT_MS : ARTICLE_SWITCH_IN_MS;

	return {
		article: renderedArticleId ? portalArticles[renderedArticleId] : null,
		openArticle: useCallback((articleId: HelpArticleId) => transitionTo(articleId), [transitionTo]),
		openArticleImmediate: useCallback((articleId: HelpArticleId) => {
			timersRef.current.forEach(window.clearTimeout);
			timersRef.current = [];
			setRenderedArticleId(articleId);
			setPhase('idle');
		}, []),
		closeArticle: useCallback(() => transitionTo(null), [transitionTo]),
		stageStyle: {
			opacity: isOut ? 0 : 1,
			transform: isOut ? 'scale(0.985)' : 'scale(1)',
			transition: `opacity ${durationMs}ms ${ARTICLE_SWITCH_EASE}, transform ${durationMs}ms ${ARTICLE_SWITCH_EASE}`,
		},
	};
};

const DEMO_ARTICLE_ID: HelpArticleId = 'vpn';

type HubCursorTarget =
	| 'start'
	| 'customizeButton'
	| 'bannerSwatch'
	| 'iconSwatch'
	| 'cardApproach'
	| 'articleCard';

const shouldRunItSupportDemo = (isActive: boolean, isAnimationComplete: boolean) =>
	isActive && !isAnimationComplete;

const shouldShowSkipAnimation = (
	isActive: boolean,
	isAnimationComplete: boolean,
	prefersReducedMotion: boolean,
) => shouldRunItSupportDemo(isActive, isAnimationComplete) && !prefersReducedMotion;

export type JsmPortalPlaygroundItSupportPreviewProps = {
	isActive?: boolean;
	hasUserTakenOver?: boolean;
	onDemoComplete?: () => void;
};

const JsmPortalPlaygroundItSupportPreviewComponent = ({
	isActive = true,
	hasUserTakenOver = false,
	onDemoComplete,
}: JsmPortalPlaygroundItSupportPreviewProps) => {
	const intl = useIntl();
	const helpCenterNameFieldId = useId();
	const rootRef = useRef<HTMLDivElement>(null);
	const demoLayer = useJsmPlaygroundDemoLayer();
	const demoLayerRef = useRef(demoLayer);
	demoLayerRef.current = demoLayer;
	const demoCursor = useDemoCursor<HubCursorTarget>('start');
	const { updateStops } = demoCursor;
	const [demoCardState, setDemoCardState] = useState<'idle' | 'hovered' | 'pressed'>('idle');
	const [shouldPlayArticleDemo, setShouldPlayArticleDemo] = useState(false);
	const [isAnimationComplete, setIsAnimationComplete] = useState(false);
	const [shouldShowCompletedChat, setShouldShowCompletedChat] = useState(false);
	const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
	const [helpCenterName, setHelpCenterName] = useState(() =>
		intl.formatMessage(messages.helpCenterTitle),
	);
	const initialHelpCenterNameRef = useRef(helpCenterName);
	const [primaryColor, setPrimaryColor] = useState<ArticleAccentColor>('lime');
	const [secondaryColor, setSecondaryColor] = useState<ArticleAccentColor>('lime');
	const { firePlaygroundScreenNavigated, fireTextFieldBlurred } =
		useJsmPlaygroundAnalytics('it_support');
	const {
		article: openArticle,
		openArticle: handleOpenArticle,
		openArticleImmediate,
		closeArticle: handleCloseArticle,
		stageStyle,
	} = usePortalArticleTransition();
	const prefersReducedMotion = usePrefersReducedMotion();
	const notifyDemoComplete = useNotifyDemoComplete(onDemoComplete);
	const hasUserTakenOverRef = useRef(hasUserTakenOver);
	hasUserTakenOverRef.current = hasUserTakenOver;
	const handleDemoChatFinished = useCallback(() => {
		if (!hasUserTakenOverRef.current) {
			setIsAnimationComplete(true);
			notifyDemoComplete();
		}
	}, [notifyDemoComplete]);

	useEffect(() => {
		if (isActive && prefersReducedMotion) {
			notifyDemoComplete();
		}
	}, [isActive, notifyDemoComplete, prefersReducedMotion]);

	const togglePrimaryColor = () => setPrimaryColor((color) => (color === 'lime' ? 'teal' : 'lime'));
	const toggleSecondaryColor = () =>
		setSecondaryColor((color) => (color === 'lime' ? 'teal' : 'lime'));

	const openArticleFromCard = (articleId: HelpArticleId) => {
		setShouldPlayArticleDemo(false);
		setShouldShowCompletedChat(false);
		firePlaygroundScreenNavigated({
			team_type: 'it_support',
			screen_id: `article_${articleId}`,
			interaction_mode: 'manual_click',
		});
		handleOpenArticle(articleId);
	};

	const closeArticle = () => {
		setShouldPlayArticleDemo(false);
		setShouldShowCompletedChat(false);
		handleCloseArticle();
	};

	const measureStops = useCallback(
		(targets: HubCursorTarget[]) => {
			const root = rootRef.current;
			const origin = demoLayerRef.current ?? root;
			if (!root || !origin) {
				return;
			}
			const find = (selector: string) => root.querySelector(selector);
			const next: Partial<Record<HubCursorTarget, DemoPoint>> = {};

			for (const target of targets) {
				if (target === 'start' || target === 'customizeButton') {
					const button = find('[data-testid="customize-help-center-button"]');
					if (button) {
						const box = rectWithin(origin, button);
						next.start = { x: box.left - 40, y: box.bottom + 24 };
						next.customizeButton = centerOf(box);
					}
				} else if (target === 'bannerSwatch' || target === 'iconSwatch') {
					const swatch = find(
						`[data-testid="${target === 'bannerSwatch' ? 'primary' : 'secondary'}-color-swatch"]`,
					);
					if (swatch) {
						next[target] = centerOf(rectWithin(origin, swatch));
					}
				} else {
					// Same node the prototype measures: the title wrapper, not an h3.
					const title = find(`[data-testid="jsm-portal-help-card-title-${DEMO_ARTICLE_ID}"]`);
					if (title) {
						const box = rectWithin(origin, title);
						next.articleCard = centerOf(box);
						next.cardApproach = { x: box.left - 56, y: box.bottom + 72 };
					}
				}
			}

			updateStops(next);
		},
		[updateStops],
	);

	useLayoutEffect(() => {
		measureStops(['start']);
	}, [measureStops, demoLayer]);

	useDemoTimeline(shouldRunItSupportDemo(isActive, isAnimationComplete), hasUserTakenOver, (at) => {
		const { updateCursor, setIsTransitionEnabled } = demoCursor;

		at(HUB_DEMO.cursorIn, () => updateCursor({ target: 'customizeButton', isVisible: true }));
		at(HUB_DEMO.buttonPress, () => {
			updateCursor({ isPressed: true });
			setIsCustomizeOpen(true);
		});
		at(HUB_DEMO.buttonRelease, () => updateCursor({ isPressed: false }));
		at(HUB_DEMO.bannerSwatchMove, () => {
			measureStops(['bannerSwatch']);
			updateCursor({ target: 'bannerSwatch' });
		});
		at(HUB_DEMO.bannerSwatchPress, () => {
			updateCursor({ isPressed: true });
			togglePrimaryColor();
		});
		at(HUB_DEMO.bannerSwatchRelease, () => updateCursor({ isPressed: false }));
		at(HUB_DEMO.iconSwatchMove, () => {
			measureStops(['iconSwatch']);
			updateCursor({ target: 'iconSwatch' });
		});
		at(HUB_DEMO.iconSwatchPress, () => {
			updateCursor({ isPressed: true });
			toggleSecondaryColor();
		});
		at(HUB_DEMO.iconSwatchRelease, () => updateCursor({ isPressed: false }));
		at(HUB_DEMO.cursorHide, () => updateCursor({ isVisible: false }));
		at(HUB_DEMO.cardApproachJump, () => {
			measureStops(['articleCard']);
			setIsTransitionEnabled(false);
			updateCursor({ target: 'cardApproach' });
		});
		at(HUB_DEMO.articleCardMove, () => {
			setIsTransitionEnabled(true);
			updateCursor({ target: 'articleCard', isVisible: true });
			setDemoCardState('hovered');
		});
		at(HUB_DEMO.articleCardPress, () => {
			updateCursor({ isPressed: true });
			setDemoCardState('pressed');
			setShouldPlayArticleDemo(true);
			handleOpenArticle(DEMO_ARTICLE_ID);
		});
		at(HUB_DEMO.articleCardRelease, () => {
			updateCursor({ isPressed: false });
			setDemoCardState('idle');
		});
		at(HUB_DEMO.cursorOut, () => updateCursor({ isVisible: false }));
	});

	const skipAnimation = () => {
		demoCursor.updateCursor({ isVisible: false, isPressed: false });
		setDemoCardState('idle');
		setIsCustomizeOpen(false);
		setPrimaryColor('teal');
		setSecondaryColor('teal');
		setShouldPlayArticleDemo(false);
		openArticleImmediate(DEMO_ARTICLE_ID);
		setShouldShowCompletedChat(true);
		setIsAnimationComplete(true);
		notifyDemoComplete();
	};

	const handleCustomizeToggle = (event?: ReactMouseEvent<HTMLElement>) => {
		if (isUntrustedClick(event)) {
			setIsCustomizeOpen((isOpen) => !isOpen);
			return;
		}
		const nextState = !isCustomizeOpen;
		firePlaygroundScreenNavigated({
			team_type: 'it_support',
			screen_id: nextState ? 'customize_sidebar' : 'help_center_hub',
			interaction_mode: 'manual_click',
		});
		setIsCustomizeOpen(nextState);
	};

	return (
		<div ref={rootRef} css={previewRootStyles}>
			<JsmPlaygroundNavigation
				config={teamNavigationConfig['it-support']}
				teamName={intl.formatMessage(messages.teamItSupport)}
				onCustomizeHelpCenterClick={handleCustomizeToggle}
			/>

			<div css={stageStyles} style={stageStyle}>
				{openArticle ? (
					<JsmPortalPlaygroundArticleView
						key={openArticle.id}
						article={openArticle}
						secondaryColor={secondaryColor}
						onBack={closeArticle}
						shouldPlayDemo={shouldPlayArticleDemo && isActive && !shouldShowCompletedChat}
						shouldCompleteChat={shouldShowCompletedChat}
						hasUserTakenOver={hasUserTakenOver}
						onDemoComplete={handleDemoChatFinished}
					/>
				) : (
					<div css={[contentRowStyles, isCustomizeOpen && contentRowOpenStyles]}>
						<div css={helpHubStyles}>
							<div css={calloutBannerStyles}>
								<div css={heroStyles}>
									{/* eslint-disable-next-line no-restricted-syntax */}
									<img css={heroArtworkStyles} src={portalHeroCardHand} alt="" aria-hidden="true" />

									<div css={heroContentStyles}>
										<div css={heroCopyStyles}>
											<div aria-level={1} css={heroHeadingStyles} role="heading">
												{helpCenterName || intl.formatMessage(messages.helpCenterTitle)}
											</div>
											<p css={heroSubtitleStyles}>
												{intl.formatMessage(messages.helpCenterSubtitle)}
											</p>
										</div>

										<div
											css={promptInlineStyles}
											aria-hidden="true"
											// @ts-expect-error -- inert is missing from @compiled/react types
											inert=""
										>
											<div css={promptFieldStyles}>
												<TextField
													appearance="none"
													aria-label={intl.formatMessage(messages.askAiPlaceholder)}
													placeholder={intl.formatMessage(messages.askAiPlaceholder)}
													isReadOnly
													tabIndex={-1}
												/>
											</div>
											<IconButton
												icon={SendButtonIcon}
												label={intl.formatMessage(messages.sendButton)}
												shape="circle"
												spacing="compact"
												tabIndex={-1}
											/>
										</div>
									</div>
								</div>
							</div>

							<section css={findHelpBandStyles}>
								<div css={bandContentStyles}>
									<div aria-level={2} css={findHelpHeadingStyles} role="heading">
										{intl.formatMessage(messages.findHelpHeading)}
									</div>

									<div css={cardRowStyles}>
										{itSupportHelpCards.map((card) => (
											<HelpArticleCard
												key={card.id}
												card={card}
												primaryColor={primaryColor}
												secondaryColor={secondaryColor}
												onOpen={() => openArticleFromCard(card.id)}
												demoState={card.id === DEMO_ARTICLE_ID ? demoCardState : 'idle'}
											/>
										))}
									</div>
								</div>
							</section>

							<JsmPortalPlaygroundRovoLauncherMark />
						</div>

						<div
							css={sidebarStyles}
							data-testid="jsm-portal-customize-sidebar"
							aria-hidden={!isCustomizeOpen}
							// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
							inert={isCustomizeOpen ? undefined : ''}
						>
							<div css={sidebarHeaderStyles}>
								<JsmPlaygroundHeading level={2} size="small">
									{intl.formatMessage(messages.customizePanelTitle)}
								</JsmPlaygroundHeading>
								<IconButton
									appearance="subtle"
									icon={CloseButtonIcon}
									label={intl.formatMessage(messages.closeButton)}
									spacing="compact"
									onClick={(event) => {
										if (!isUntrustedClick(event)) {
											firePlaygroundScreenNavigated({
												team_type: 'it_support',
												screen_id: 'help_center_hub',
												interaction_mode: 'manual_click',
											});
										}
										setIsCustomizeOpen(false);
									}}
								/>
							</div>

							<div css={sidebarBodyStyles}>
								<SidebarField
									label={intl.formatMessage(messages.helpCenterNameField)}
									htmlFor={helpCenterNameFieldId}
								>
									<div css={helpCenterNameFieldStyles}>
										<TextField
											id={helpCenterNameFieldId}
											value={helpCenterName}
											onChange={(event: ChangeEvent<HTMLInputElement>) =>
												setHelpCenterName(event.target.value)
											}
											onBlur={(event: FocusEvent<HTMLInputElement>) => {
												const currentVal = event.target.value;
												const hasChanged = currentVal !== initialHelpCenterNameRef.current;
												fireTextFieldBlurred({
													screenId: 'help_center_preview',
													elementId: 'helpCenterName',
													actionSubjectId: 'helpCenterName',
													hasChanged,
													inputLength: currentVal.length,
													teamType: 'it_support',
												});
											}}
										/>
									</div>
								</SidebarField>

								<SidebarField label={intl.formatMessage(messages.logoField)}>
									{/* eslint-disable-next-line no-restricted-syntax */}
									<img css={sidebarLogoStyles} src={customizeSidebarLogo} alt="" />
								</SidebarField>

								<SidebarField label={intl.formatMessage(messages.bannerField)}>
									{/* eslint-disable-next-line no-restricted-syntax */}
									<img css={sidebarBannerStyles} src={customizeSidebarBanner} alt="" />
								</SidebarField>

								<SidebarField label={intl.formatMessage(messages.textColorField)}>
									<div css={[colorSwatchStyles, textColorSwatchStyles]} />
								</SidebarField>

								<SidebarField label={intl.formatMessage(messages.primaryColorField)}>
									<button
										type="button"
										css={[
											colorSwatchStyles,
											colorSwatchButtonStyles,
											primaryColor === 'teal' ? primarySwatchTealStyles : primarySwatchLimeStyles,
										]}
										aria-label={intl.formatMessage(messages.togglePrimaryColor)}
										data-testid="primary-color-swatch"
										onClick={togglePrimaryColor}
									/>
								</SidebarField>

								<SidebarField label={intl.formatMessage(messages.secondaryColorField)}>
									<button
										type="button"
										css={[
											colorSwatchStyles,
											colorSwatchButtonStyles,
											secondaryColor === 'teal'
												? secondarySwatchTealStyles
												: secondarySwatchLimeStyles,
										]}
										aria-label={intl.formatMessage(messages.toggleSecondaryColor)}
										data-testid="secondary-color-swatch"
										onClick={toggleSecondaryColor}
									/>
								</SidebarField>
							</div>
						</div>
					</div>
				)}
			</div>

			<JsmPlaygroundDemoCursor
				label={DEMO_CURSOR_LABEL}
				testId="jsm-portal-demo-cursor"
				container={demoLayer}
				hasUserTakenOver={hasUserTakenOver}
				isPressed={demoCursor.isPressed}
				isTransitionEnabled={demoCursor.isTransitionEnabled}
				isVisible={demoCursor.isVisible && isActive}
				x={demoCursor.position.x}
				y={demoCursor.position.y}
			/>

			<JsmPlaygroundSkipAnimationButton
				isVisible={shouldShowSkipAnimation(isActive, isAnimationComplete, prefersReducedMotion)}
				label={intl.formatMessage(messages.skipAnimation)}
				onClick={skipAnimation}
				placement="start"
			/>
		</div>
	);
};

// Memoised so the playground root's cursor state changes (enter, hover, press, leave) do not
// re-render this subtree. Its props are primitives plus a stable callback.
export const JsmPortalPlaygroundItSupportPreview = memo(
	JsmPortalPlaygroundItSupportPreviewComponent,
);

const HelpArticleCard = ({
	card,
	primaryColor,
	secondaryColor,
	onOpen,
	demoState,
}: {
	card: HelpCard;
	primaryColor: ArticleAccentColor;
	secondaryColor: ArticleAccentColor;
	onOpen: () => void;
	demoState: 'idle' | 'hovered' | 'pressed';
}) => {
	const intl = useIntl();
	const title = intl.formatMessage(card.title);

	return (
		<button
			type="button"
			css={[
				cardStyles,
				demoState === 'hovered' && cardHoveredStyles,
				demoState === 'pressed' && cardPressedStyles,
			]}
			data-testid={`jsm-portal-help-card-${card.id}`}
			aria-label={intl.formatMessage(messages.readArticle, { title })}
			onClick={onOpen}
		>
			<div
				css={[
					cardHeaderStyles,
					primaryColor === 'teal' ? cardHeaderTealStyles : cardHeaderLimeStyles,
				]}
			>
				<PortalArticleIconTile color={secondaryColor} size="xlarge" />
			</div>

			<div css={cardBodyStyles}>
				<div css={cardHeadlineStyles}>
					<Lozenge appearance={card.tagAppearance} testId={`jsm-portal-help-card-tag-${card.id}`}>
						{intl.formatMessage(card.tag)}
					</Lozenge>
					<div data-testid={`jsm-portal-help-card-title-${card.id}`}>
						<JsmPlaygroundHeading level={3} size="small">
							{title}
						</JsmPlaygroundHeading>
					</div>
				</div>

				<Text maxLines={2}>{intl.formatMessage(card.description)}</Text>

				<div css={bylineStyles}>
					<span css={bylineAttributionStyles}>
						<RovoIcon appearance="brand" label="" shouldUseHexLogo size="xxsmall" />
						<Text size="small" color="color.text.subtlest">
							{intl.formatMessage(messages.createdWithRovo)}
						</Text>
					</span>
					<span css={bylineDateStyles}>
						<Text size="small" color="color.text.subtlest">
							{intl.formatMessage(messages.lastUpdated, {
								date: formatCardDate(card.lastUpdated),
							})}
						</Text>
					</span>
				</div>
			</div>
		</button>
	);
};

const SidebarField = ({
	label,
	htmlFor,
	children,
}: {
	label: string;
	htmlFor?: string;
	children: ReactNode;
}) => (
	<div css={sidebarFieldStyles}>
		{htmlFor ? (
			<label css={sidebarFieldLabelStyles} htmlFor={htmlFor}>
				<Text size="small" weight="bold" color="color.text.subtle">
					{label}
				</Text>
			</label>
		) : (
			<Text size="small" weight="bold" color="color.text.subtle">
				{label}
			</Text>
		)}
		{children}
	</div>
);

const previewRootStyles = css({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
	overflow: 'hidden',
});

const stageStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	minHeight: 0,
	minWidth: 0,
	width: '100%',
	overflow: 'hidden',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none !important',
		transform: 'none !important',
	},
});

const contentRowStyles = css({
	display: 'grid',
	gridTemplateColumns: '1fr 0px',
	flexGrow: 1,
	minHeight: 0,
	width: '100%',
	overflow: 'hidden',
	transition: 'grid-template-columns 300ms ease',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const contentRowOpenStyles = css({
	gridTemplateColumns: '1fr 268px',
});

const helpHubStyles = css({
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	minWidth: 0,
	minHeight: 0,
	width: '100%',
	backgroundColor: token('elevation.surface'),
	overflow: 'hidden',
});

const calloutBannerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	boxSizing: 'border-box',
	width: '100%',
	paddingBlockStart: token('space.300'),
	paddingBlockEnd: token('space.150'),
	paddingInline: token('space.300'),
	backgroundColor: token('elevation.surface'),
});

const heroStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	position: 'relative',
	boxSizing: 'border-box',
	width: '100%',
	maxWidth: '1202px',
	minHeight: '317px',
	paddingBlockStart: token('space.500'),
	paddingInline: token('space.800'),
	borderRadius: token('radius.large'),
	backgroundImage: BEACON_GRADIENT,
	overflow: 'hidden',
});

const heroArtworkStyles = css({
	position: 'absolute',
	insetBlockEnd: 0,
	insetInlineEnd: '-24px',
	width: 'auto',
	height: '100%',
	pointerEvents: 'none',
	userSelect: 'none',
});

const heroContentStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
	position: 'relative',
	zIndex: 1,
	boxSizing: 'border-box',
	width: '100%',
	maxWidth: '1081px',
});

const heroCopyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
});

const heroHeadingStyles = css({
	margin: 0,
	maxWidth: '700px',
	color: token('color.text'),
	fontFamily: BRAND_FONT,
	fontSize: '64px',
	fontWeight: 700,
	lineHeight: 1.17,
	letterSpacing: '-0.02em',
});

const heroSubtitleStyles = css({
	margin: 0,
	maxWidth: '502px',
	color: token('color.text'),
	fontFamily: `${BRAND_FONT} !important`,
	fontSize: '24px !important',
	fontWeight: '400 !important',
	lineHeight: '1.17 !important',
});

const promptInlineStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.150'),
	boxSizing: 'border-box',
	width: '100%',
	maxWidth: '533px',
	paddingBlock: token('space.150'),
	paddingInline: token('space.200'),
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
	boxShadow: token('elevation.shadow.raised'),
});

const promptFieldStyles = css({
	flexGrow: 1,
	minWidth: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& input': {
		font: token(
			'font.body',
			'normal 400 14px/20px "Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
		),
	},
});

const findHelpBandStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	flexGrow: 1,
	flexShrink: 0,
	gap: token('space.200'),
	boxSizing: 'border-box',
	width: '100%',
	paddingBlockStart: token('space.200'),
	paddingBlockEnd: token('space.300'),
	paddingInline: token('space.300'),
	backgroundColor: token('elevation.surface'),
});

const bandContentStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: token('space.150'),
	boxSizing: 'border-box',
	width: '100%',
	maxWidth: '1200px',
});

const findHelpHeadingStyles = css({
	margin: 0,
	color: token('color.text'),
	fontFamily: BRAND_FONT,
	fontSize: '24px',
	fontWeight: 700,
	lineHeight: 1.17,
	textAlign: 'center',
});

const cardRowStyles = css({
	display: 'flex',
	alignItems: 'stretch',
	gap: token('space.200'),
	width: '100%',
});

const cardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flexBasis: 0,
	flexGrow: 1,
	flexShrink: 1,
	minWidth: 0,
	padding: 0,
	boxSizing: 'border-box',
	textAlign: 'start',
	cursor: 'pointer',
	backgroundColor: token('elevation.surface'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.xlarge'),
	overflow: 'hidden',
	transition: 'box-shadow 150ms ease, transform 150ms ease',
	'&:hover': {
		boxShadow: token('elevation.shadow.overlay'),
		transform: 'translateY(-4px)',
	},
	'&:active': {
		boxShadow: token('elevation.shadow.raised'),
		transform: 'translateY(-1px)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const cardHoveredStyles = css({
	boxShadow: token('elevation.shadow.overlay'),
	transform: 'translateY(-4px)',
});

const cardPressedStyles = css({
	boxShadow: token('elevation.shadow.raised'),
	transform: 'translateY(-1px)',
});

const cardHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	width: '100%',
	boxSizing: 'border-box',
	paddingBlock: token('space.150'),
	paddingInline: token('space.300'),
});

const cardHeaderLimeStyles = css({
	backgroundColor: token('color.background.accent.lime.subtlest'),
});

const cardHeaderTealStyles = css({
	backgroundColor: token('color.background.accent.teal.subtlest'),
});

const cardBodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	gap: token('space.200'),
	padding: token('space.300'),
});

const cardHeadlineStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.200'),
});

const bylineStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flexWrap: 'wrap',
	gap: token('space.050'),
	width: '100%',
	marginBlockStart: 'auto',
	overflow: 'hidden',
});

const bylineAttributionStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	flexShrink: 0,
	whiteSpace: 'nowrap',
});

const bylineDateStyles = css({
	flexShrink: 0,
	whiteSpace: 'nowrap',
});

const sidebarStyles = css({
	display: 'flex',
	flexDirection: 'column',
	minWidth: 0,
	minHeight: 0,
	height: '100%',
	boxSizing: 'border-box',
	backgroundColor: token('elevation.surface.overlay'),
	borderInlineStartWidth: token('border.width', '1px'),
	borderInlineStartStyle: 'solid',
	borderInlineStartColor: token('color.border'),
	overflow: 'hidden',
});

const sidebarHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flexShrink: 0,
	boxSizing: 'border-box',
	padding: token('space.200'),
});

const sidebarBodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
	boxSizing: 'border-box',
	padding: token('space.200'),
	paddingBlockStart: 0,
	overflowY: 'auto',
});

const sidebarFieldStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
});

// @atlaskit/textfield in wac-web hardcodes a 3px radius. The current field uses radius.medium.
const helpCenterNameFieldStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [data-ds--text-field--container]': {
		borderRadius: token('radius.medium', '6px'),
		borderWidth: '1px',
	},
});

const sidebarFieldLabelStyles = css({
	display: 'inline-flex',
});

const sidebarLogoStyles = css({
	width: '47px',
	height: '47px',
	borderRadius: token('radius.small'),
	objectFit: 'cover',
});

const sidebarBannerStyles = css({
	width: '100%',
	aspectRatio: '2404 / 634',
	borderRadius: token('radius.medium'),
	objectFit: 'cover',
});

const colorSwatchStyles = css({
	width: '53px',
	height: '27px',
	boxSizing: 'border-box',
	borderRadius: token('radius.small'),
});

const colorSwatchButtonStyles = css({
	padding: 0,
	borderStyle: 'none',
	cursor: 'pointer',
});

const textColorSwatchStyles = css({
	backgroundColor: token('color.text'),
});

const primarySwatchLimeStyles = css({
	backgroundColor: token('color.background.accent.lime.subtlest'),
});

const primarySwatchTealStyles = css({
	backgroundColor: token('color.background.accent.teal.subtlest'),
});

const secondarySwatchLimeStyles = css({
	backgroundColor: token('color.icon.accent.lime'),
});

const secondarySwatchTealStyles = css({
	backgroundColor: token('color.icon.accent.teal'),
});
